<script setup lang="ts">
import { computed, ref } from 'vue'

import { REPORT_TOOLBAR_ICONS } from '../../configs/reportToolbarIcons'
import type { ReportColumn } from '../../models/column.types'
import {
  IMPLEMENTED_REPORT_TABLE_TYPES,
  isImplementedReportTableType,
  type ReportTableType,
} from '../../models/tablePresentation.types'
import type { TrendTableFrequency } from '../../models/trendTable.types'
import { toReportColumnOption } from '../../service/reportColumnOption'

import ReportToolbarSelect from './ReportToolbarSelect.vue'

import { useI18n } from '@/shared/i18n'
import {
  Button,
  Icon,
  Input,
  Menu,
  Modal,
  Select,
  type MenuItemOption,
  type SelectOption,
} from '@/shared/ui'

const tableType = defineModel<ReportTableType>('tableType', { default: 'table' })
const breakdown = defineModel<string | null>('breakdown', { default: null })
const frequency = defineModel<TrendTableFrequency>('frequency', { default: 'daily' })
const savedViewId = defineModel<string | null>('savedViewId', { default: null })
const props = withDefaults(
  defineProps<{
    columns: readonly ReportColumn[]
    tableTypes?: readonly ReportTableType[]
    savedViews?: readonly { id: string; name: string }[]
    compact?: boolean
  }>(),
  {
    tableTypes: () => [...IMPLEMENTED_REPORT_TABLE_TYPES],
    compact: false,
  },
)

const { t } = useI18n()
const STABLE_VIEW_ID = '__stable-view__'
const renameOpen = ref(false)
const renameValue = ref('')
const renameAttempted = ref(false)
const selectedSavedView = computed(() =>
  (props.savedViews ?? []).find((view) => view.id === savedViewId.value),
)
const canManageSelectedView = computed(
  () => selectedSavedView.value !== undefined && savedViewId.value !== STABLE_VIEW_ID,
)
const savedViewActions = computed<MenuItemOption[]>(() => [
  { id: 'rename', label: t('reports.table.renameSavedView'), icon: 'pen' },
  { id: 'delete', label: t('reports.table.deleteSavedView'), icon: 'trash', danger: true },
])
const renameError = computed(() => renameAttempted.value && renameValue.value.trim() === '')
const tableTypeKeys: Record<ReportTableType, string> = {
  table: 'table',
  summary_table: 'summaryTable',
  leaderboard_table: 'leaderboardTable',
  comparison_table: 'comparisonTable',
  matrix_table: 'matrixTable',
  trend_table: 'trendTable',
}
const NO_BREAKDOWN = '__none__'
const breakdownSelection = computed({
  get: () => breakdown.value ?? NO_BREAKDOWN,
  set: (value: string | null) => {
    breakdown.value = !value || value === NO_BREAKDOWN ? null : value
  },
})
const tableTypeOptions = computed<SelectOption<ReportTableType>[]>(() =>
  props.tableTypes
    .filter(isImplementedReportTableType)
    .map((value) => ({ value, label: t(`reports.table.types.${tableTypeKeys[value]}`) })),
)
const breakdownOptions = computed<SelectOption<string>[]>(() => [
  { value: NO_BREAKDOWN, label: t('reports.table.noBreakdown') },
  ...props.columns
    .filter((column) => !column.numeric && !column.isImage)
    .map((column) => toReportColumnOption(column)),
])
const savedViewOptions = computed<SelectOption<string>[]>(() =>
  (props.savedViews ?? []).map((view) => ({ value: view.id, label: view.name })),
)
const frequencyOptions = computed<SelectOption<TrendTableFrequency>[]>(() =>
  (['daily', 'weekly', 'monthly'] as const).map((value) => ({
    value,
    label: t(`reports.table.frequencies.${value}`),
  })),
)

const emit = defineEmits<{
  renameSavedView: [value: { id: string; name: string }]
  removeSavedView: [id: string]
}>()

function chooseSavedViewAction(item: MenuItemOption): void {
  const selected = selectedSavedView.value
  if (!selected) return
  if (item.id === 'rename') {
    renameValue.value = selected.name
    renameAttempted.value = false
    renameOpen.value = true
  } else if (item.id === 'delete') {
    emit('removeSavedView', selected.id)
  }
}

function renameSavedView(): void {
  renameAttempted.value = true
  const selected = selectedSavedView.value
  const name = renameValue.value.trim()
  if (!selected || !name) return
  emit('renameSavedView', { id: selected.id, name })
  renameOpen.value = false
}
</script>

<template>
  <div
    class="report-table-mode-controls flex items-center gap-sm min-w-0 whitespace-nowrap"
    :class="{ 'is-compact': compact }"
  >
    <template v-if="compact">
      <ReportToolbarSelect
        v-model="tableType"
        :options="tableTypeOptions"
        :label="t('reports.table.tableType')"
        :placeholder="t('reports.table.selectTableType')"
        :icon="REPORT_TOOLBAR_ICONS.tableType"
      />
      <ReportToolbarSelect
        v-model="breakdownSelection"
        wide
        searchable
        :options="breakdownOptions"
        :label="t('reports.table.breakdown')"
        :placeholder="t('reports.table.selectBreakdown')"
        :icon="REPORT_TOOLBAR_ICONS.breakdown"
      />
      <ReportToolbarSelect
        v-if="tableType === 'trend_table'"
        v-model="frequency"
        :options="frequencyOptions"
        :label="t('reports.table.frequency')"
        :placeholder="t('reports.table.selectFrequency')"
        :icon="REPORT_TOOLBAR_ICONS.frequency"
      />
      <ReportToolbarSelect
        v-if="savedViews !== undefined"
        v-model="savedViewId"
        wide
        :options="savedViewOptions"
        :label="t('reports.table.savedViews')"
        :placeholder="t('reports.table.selectSavedView')"
        :clear-label="t('reports.table.clearSavedView')"
        :empty-label="t('reports.table.noSavedViews')"
        clearable
        :icon="REPORT_TOOLBAR_ICONS.savedViews"
      />
    </template>
    <template v-else>
      <Select
        v-model="tableType"
        :options="tableTypeOptions"
        :label="t('reports.table.tableType')"
        :placeholder="t('reports.table.selectTableType')"
        size="sm"
      />
      <Select
        v-model="breakdownSelection"
        :options="breakdownOptions"
        :label="t('reports.table.breakdown')"
        :placeholder="t('reports.table.selectBreakdown')"
        :clear-label="t('reports.table.clearBreakdown')"
        :clearable="true"
        size="sm"
      />
      <Select
        v-if="tableType === 'trend_table'"
        v-model="frequency"
        :options="frequencyOptions"
        :label="t('reports.table.frequency')"
        :placeholder="t('reports.table.selectFrequency')"
        size="sm"
      />
      <Select
        v-if="savedViews !== undefined"
        v-model="savedViewId"
        :options="savedViewOptions"
        :label="t('reports.table.savedViews')"
        :placeholder="t('reports.table.selectSavedView')"
        :empty-label="t('reports.table.noSavedViews')"
        :clear-label="t('reports.table.clearSavedView')"
        clearable
        size="sm"
      />
    </template>
    <Menu
      v-if="canManageSelectedView"
      :label="t('reports.table.savedViewActions')"
      :items="savedViewActions"
      trigger="icon"
      size="sm"
      placement="bottom-end"
      @select="chooseSavedViewAction"
    >
      <template #trigger><Icon name="ellipsis-vertical" :size="14" aria-hidden="true" /></template>
    </Menu>
    <Modal
      v-model="renameOpen"
      size="sm"
      :title="t('reports.table.renameSavedView')"
      :close-label="t('reports.actions.close')"
    >
      <Input
        v-model="renameValue"
        :label="t('reports.table.viewName')"
        :error="renameError"
        v-bind="renameError ? { errorMessage: t('reports.table.viewNameRequired') } : {}"
        @enter="renameSavedView"
      />
      <template #buttons>
        <div class="flex justify-end gap-sm">
          <Button variant="outline" @click="renameOpen = false">{{ t('common.cancel') }}</Button>
          <Button variant="primary" @click="renameSavedView">{{ t('common.save') }}</Button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<style scoped>
.report-table-mode-controls.is-compact {
  gap: 5px;
}
</style>
